import { type NetworkType, useIntl } from '@capsicum/common';
import { Button, ButtonSection, InfoTooltip, TextButton, useRouter } from '@capsicum/common-web';
import { AddRecipientCard, RecipientCard } from '@capsicum/features/digital-collectibles/web';
import {
	useWalletWithdrawRecipients,
	useWithdrawDataAtom,
	type WalletRecipientItem
} from '@capsicum/features/wallet/shared';
import { RecipientCardSkeleton } from 'libs/features/digital-collectibles/web/src/ui/recipient-card/RecipientCardSkeleton';
import { useState } from 'react';
import styled from 'styled-components';

export const RecipientsList = () => {
	const [selectedRecipient, setSelectedRecipient] = useState<undefined | WalletRecipientItem>(undefined);

	const { push } = useRouter();
	const { selectedNetworkType: networkType } = useWithdrawDataAtom();

	const { data: recipients, isLoading } = useWalletWithdrawRecipients(networkType as NetworkType);

	const { $t } = useIntl();

	const texts = {
		addRecipient: $t('wallet.withdraw.recipient.recipientList.addRecipientLink'),
		findRecipient: $t('wallet.withdraw.recipient.recipientList.addRecipientText'),
		onlyCompatible: $t('wallet.withdraw.recipient.recipientList.subtitle'),
		selectButtonLabel: $t('wallet.withdraw.recipient.recipientList.selectButtonLabel'),
		title: $t('wallet.withdraw.recipient.recipientList.title'),
		tooltipContent: $t('wallet.withdraw.recipient.recipientList.tooltipContent'),
		tooltipTitle: $t('wallet.withdraw.recipient.recipientList.tooltipTitle')
	};

	return (
		<S.Wrapper>
			<S.Container>
				<S.Title>{texts.title}</S.Title>
				<S.TextContainer>
					<S.Text>{texts.onlyCompatible}</S.Text>
					<InfoTooltip>
						<S.TooltipTitle>{texts.tooltipTitle}</S.TooltipTitle>
						<S.TooltipText>{texts.tooltipContent}</S.TooltipText>
					</InfoTooltip>
				</S.TextContainer>
				<S.TextContainer $isLoading={isLoading}>
					<S.Text>{texts.findRecipient}</S.Text>
					<S.AddRecipientButton
						testID="wallet-recipientsList-addRecipient-textButton"
						text={texts.addRecipient}
						onClick={() => {
							push(`/wallet/withdraw/recipients/create?selectedNetwork=${networkType}`);
						}}
					/>
				</S.TextContainer>

				<S.ListContainer>
					{isLoading ? (
						<RecipientCardSkeleton />
					) : (
						recipients?.map(recipient => {
							return (
								<RecipientCard
									key={recipient.id}
									recipient={recipient}
									selectedRecipient={selectedRecipient}
									onEditRecipient={() => {
										push(
											`/wallet/withdraw/recipients/edit/${recipient.id}?selectedNetwork=${networkType}`
										);
									}}
									onSelectRecipient={setSelectedRecipient}
								/>
							);
						})
					)}
				</S.ListContainer>

				{networkType && (
					<AddRecipientCard
						network={networkType as NetworkType}
						onCardClick={() => push(`/wallet/withdraw/recipients/create?selectedNetwork=${networkType}`)}
					/>
				)}
			</S.Container>
			<S.ButtonWrapper>
				<Button
					disabled={!selectedRecipient}
					isLoading={isLoading}
					testID="wallet-recipientsList-selectRecipient-submitButton"
					text={texts.selectButtonLabel}
					onClick={() => {
						push(`/wallet/withdraw/confirmation/${selectedRecipient?.id}?selectedNetwork=${networkType}`);
					}}
				/>
			</S.ButtonWrapper>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex-direction: column;
		width: 100%;
	`;
	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.large} ${({ theme }) => theme.spacings.small};
		width: 100%;
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => theme.spacings.small};
			margin-bottom: ${({ theme }) => theme.spacings.xxxlarge};
			overflow: hidden;
		}
	`;

	export const Title = styled.p`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.headings.title5};
		margin: 0 0 ${({ theme }) => theme.spacings.medium};
	`;
	export const TextContainer = styled.div<{ $isLoading?: boolean }>`
		display: flex;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		margin: 0 0 ${({ theme }) => theme.spacings.small};
		align-items: center;
		visibility: ${({ $isLoading }) => ($isLoading ? 'hidden' : 'visible')};
	`;

	export const Text = styled.p`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text3};
		margin: 0;
	`;

	export const AddRecipientButton = styled(TextButton)`
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			height: 40px;
		}
	`;

	export const ButtonWrapper = styled(ButtonSection)`
		${({ theme }) => theme.breakpoints.queries.above.tabletSmallDesktop} {
			margin-top: 0;
			padding-bottom: ${({ theme }) => theme.spacings.xxlarge};
		}
	`;

	export const ListContainer = styled.div`
		display: flex;
		flex-direction: column;
		padding-bottom: ${({ theme }) => theme.spacings.large};
		width: 100%;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;
	export const TooltipTitle = styled.span`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.headings.title6};
		margin: 0 0 ${({ theme }) => theme.spacings.xxsmall};
		display: block;
	`;
	export const TooltipText = styled.span`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text3};
		display: block;
		margin: 0;
	`;
}
